import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  Card,
  Form,
  Input,
  Button,
  DatePicker,
  Upload,
  message,
  Spin,
  Row,
  Col,
  Avatar,
  Divider,
} from "antd";
import {
  UploadOutlined,
  ArrowLeftOutlined,
  SaveOutlined,
  UserOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import ajaxService from "../../services/ajax-service";

const ProfileEditForm = () => {
  const id = 1; // replace with params if needed
  const history = useHistory();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const fetchUser = useCallback(async (userId) => {
    setLoading(true);
    try {
      const response = await ajaxService.get(`/users/${userId}`);
      if (response?.success) {
        setUser(response.data || {});
      } else {
        message.error(response?.message || "Failed to fetch user data");
      }
    } catch {
      message.error("Error fetching user data");
    } finally {
      setLoading(false);
    }
  }, []);

  const avatarSrc = useMemo(() => {
    const src = user?.profile_image;
    return src && String(src).trim() ? src : undefined;
  }, [user?.profile_image]);

  useEffect(() => {
    if (id) fetchUser(id);
  }, [id, fetchUser]);

  // ✅ Prefill form
  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        name: user.name || "",
        user_name: user.user_name || "",
        email: user.email || "",
        phone: user.phone || "",
        bio: user.bio || "",
        dob: user.dob ? dayjs(user.dob) : null,
      });

      if (user.profile_image) {
        setAvatarUrl(user.profile_image);
      }
    }
  }, [user, form]);

  // Sidebar listener
  useEffect(() => {
    const handleSidebarCollapse = (event) => {
      setSidebarCollapsed(event.detail.collapsed);
    };
    window.addEventListener("sidebarCollapse", handleSidebarCollapse);
    return () =>
      window.removeEventListener("sidebarCollapse", handleSidebarCollapse);
  }, []);

  // 🔍 Username uniqueness check
  const checkUsernameUnique = async (username) => {
    try {
      const res = await ajaxService.post("/check-username", { username, id });
      return res?.success;
    } catch {
      return false;
    }
  };

  const uploadAvatar = async (file) => {
    if (!id) return;
    const formData = new FormData();
    formData.append("profile_image", file);
    try {
      const res = await ajaxService.postFormData(
        `/edit-user-profile/`,
        id,
        formData
      );
      if (res?.success) {
        setAvatarUrl(res.data.profile_image || avatarUrl);
        setAvatarFile(null);
        message.success("Profile picture uploaded successfully");
      } else {
        message.error(res?.message || "Upload failed");
      }
    } catch {
      message.error("Error uploading profile picture");
    }
  };

  // ✅ Avatar format validation
  const handleAvatarUpload = async (file) => {
    const isValid =
      file.type === "image/jpeg" ||
      file.type === "image/jpg" ||
      file.type === "image/png";

    if (!isValid) {
      message.error("Only JPG, JPEG, and PNG formats are allowed");
      return Upload.LIST_IGNORE;
    }

    if (avatarUrl && avatarUrl.startsWith("blob:")) {
      URL.revokeObjectURL(avatarUrl);
    }

    const newUrl = URL.createObjectURL(file);
    setAvatarUrl(newUrl);
    setAvatarFile(file);
    await uploadAvatar(file);
    return false;
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const phoneDigits =
        values.phone && String(values.phone).trim() !== ""
          ? String(values.phone).replace(/\D/g, "")
          : null;

      const payload = {
        ...values,
        dob: values.dob ? values.dob.format("YYYY-MM-DD") : null,
        phone: phoneDigits && phoneDigits.length > 0 ? phoneDigits : null,
      };

      const baseUrl = `/users/`;
      let res;

      if (avatarFile) {
        const formData = new FormData();
        Object.entries(payload).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== "") {
            formData.append(k, v);
          }
        });
        formData.append("profile_image", avatarFile);
        res = await ajaxService.putFormData(baseUrl, id, formData);
      } else {
        res = await ajaxService.put(baseUrl, id, payload);
      }

      if (res?.success) {
        message.success("User updated successfully");
        history.push(`/profile`);
      } else {
        message.error(res?.message || "Update failed");
      }
    } catch {
      message.error("Error updating user");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        className="page-container"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div
      className={`page-container ${
        sidebarCollapsed ? "sidebar-collapsed" : ""
      }`}
    >
      <div className="module-header">
        <h1 className="module-title">Update Profile</h1>
        <p className="module-subtitle">
          Edit and update your profile information
        </p>
      </div>

      <Card
        style={{
          backgroundColor: "var(--dark-bg-card)",
          border: "1px solid rgba(254, 2, 125, 0.2)",
          borderRadius: "var(--radius-lg)",
        }}
        extra={
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => history.goBack()}
            style={{
              backgroundColor: "var(--primary-purple)",
              borderColor: "var(--primary-purple)",
              color: "white",
            }}
          >
            Back
          </Button>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {/* Avatar */}
          <div
            style={{
              position: "relative",
              display: "inline-block",
              marginBottom: 24,
            }}
          >
            <Avatar
              size={100}
              src={avatarUrl ?? avatarSrc}
              icon={!avatarUrl && <UserOutlined />}
              style={{ border: "1px solid rgba(254, 2, 125, 0.2)" }}
            />
            <div style={{ position: "absolute", bottom: -8, right: -8 }}>
              <Upload
                name="profile_image"
                showUploadList={false}
                beforeUpload={handleAvatarUpload}
              >
                <Button
                  type="primary"
                  size="small"
                  shape="circle"
                  icon={<UploadOutlined />}
                  style={{
                    background: "var(--primary-purple)",
                    borderColor: "var(--primary-purple)",
                  }}
                />
              </Upload>
            </div>
          </div>

          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Card
                size="small"
                style={{ background: "var(--dark-bg-card)", marginBottom: 16 }}
              >
                <p style={{ fontWeight: 600, color: "var(--text-white)" }}>
                  Profile Information
                </p>
                <Divider />

                {/* ✅ Name */}
                <Form.Item
                  name="name"
                  label={<span style={{ color: "var(--text-white)" }}>Full Name</span>}
                  rules={[
                    { required: true, message: "Name is required" },
                    {
                      max: 50,
                      message: "Name cannot exceed 50 characters",
                    },
                    {
                      pattern: /^[A-Za-z0-9]+( [A-Za-z0-9]+)*$/,
                      message:
                        "Name can contain only alphanumeric characters and single spaces between words",
                    },
                  ]}
                  tooltip={{
                    title: "This will appear on the profile",
                    icon: <InfoCircleOutlined />,
                  }}
                >
                  <Input
                    className="input-field"
                    onBlur={(e) => {
                      const trimmed = e.target.value.replace(/^\s+/, "");
                      form.setFieldsValue({ name: trimmed });
                    }}
                  />
                </Form.Item>

                {/* ✅ Username */}
                {/* <Form.Item
                  name="user_name"
                  label={
                    <span style={{ color: "var(--text-white)" }}>
                      Username
                    </span>
                  }
                  rules={[
                    { required: true, message: "Username is required" },
                    { max: 20, message: "Max 20 characters allowed" },
                    {
                      pattern: /^[a-zA-Z0-9\-!@#$%^&*()_+=]+$/,
                      message: "Invalid characters in username",
                    },
                    {
                      validator: async (_, value) => {
                        if (!value) return Promise.resolve();
                        const isUnique = await checkUsernameUnique(value);
                        if (false) {
                          return Promise.reject(
                            new Error("Username already exists")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input className="input-field" />
                </Form.Item> */}

                {/* ✅ DOB */}
                <Form.Item
                  name="dob"
                  label={
                    <span style={{ color: "var(--text-white)" }}>
                      Date of Birth
                    </span>
                  }
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const age = dayjs().diff(value, "year");
                        if (age < 18) {
                          return Promise.reject(
                            new Error(
                              "You must be at least 18 years old"
                            )
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    className="input-field"
                    disabled
                  />
                </Form.Item>

                {/* ✅ Bio */}
                <Form.Item
                  name="bio"
                  label={
                    <span style={{ color: "var(--text-white)" }}>Bio</span>
                  }
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const words = value
                          .trim()
                          .split(/\s+/)
                          .filter(Boolean);
                        if (words.length > 200) {
                          return Promise.reject(
                            new Error("Bio cannot exceed 200 words")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input.TextArea
                    rows={3}
                    className="input-field"
                    style={{ resize: "none" }}
                  />
                </Form.Item>
              </Card>
            </Col>

            <Col xs={24} md={12}>
              <Card
                size="small"
                style={{ background: "var(--dark-bg-card)", marginBottom: 16 }}
              >
                <p style={{ fontWeight: 600, color: "var(--text-white)" }}>
                  Contact Details
                </p>
                <Divider />

                {/* ✅ Email (disabled) */}
                <Form.Item
                  name="email"
                  label={
                    <span style={{ color: "var(--text-white)" }}>Email</span>
                  }
                  rules={[
                    { type: "email", message: "Invalid email" },
                  ]}
                >
                  <Input className="input-field" disabled />
                </Form.Item>

                {/* ✅ Phone */}
                <Form.Item
                  name="phone"
                  label={
                    <span style={{ color: "var(--text-white)" }}>Phone</span>
                  }
                  rules={[
                    {
                      pattern: /^[0-9]{6,20}$/,
                      message: "Phone must be digits only",
                    },
                  ]}
                  normalize={(value) => (value ? String(value).replace(/\D/g, "") : value)}
                >
                  <Input className="input-field" placeholder="Enter phone number" inputMode="numeric" />
                </Form.Item>
              </Card>
            </Col>
          </Row>

          <div style={{ textAlign: "right", marginTop: 24 }}>
            <Button onClick={() => history.goBack()} style={{ marginRight: 12 }}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={submitting}
              style={{
                backgroundColor: "var(--primary-purple)",
                borderColor: "var(--primary-purple)",
              }}
            >
              Save Changes
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default ProfileEditForm;
